import Papa from "papaparse";
import type { CSVMapping, CSVSummary, StandardField } from "./types";
import { detectTransfer } from "./transfer-detection";
import { isLoanPrincipalType } from "./loan-detection";
import { DISPLAY_TIME_ZONE } from "@/lib/utils";
import { isInternalTransfer, isLoanPrincipalRow } from "@/lib/csv/money";

/**
 * Parse CSV content into headers and rows
 */
export function parseCSV(content: string): {
  headers: string[];
  rows: Record<string, string>[];
} {
  const result = Papa.parse<Record<string, string>>(content, {
    header: true,
    skipEmptyLines: true,
    transformHeader: (h) => h.trim(),
  });
  return {
    headers: result.meta.fields || [],
    rows: result.data,
  };
}

/**
 * Clean currency string to number (ported from melbudget's clean_decimal)
 * Handles: $1,234.56  ($1,234.56)  -1234.56  1234
 */
export function cleanDecimal(value: string | null | undefined): number {
  if (!value || value.trim() === "") return 0;

  let cleaned = String(value).trim();

  // Handle parentheses for negatives: ($1,234.56) -> -1234.56
  if (cleaned.startsWith("(") && cleaned.endsWith(")")) {
    cleaned = "-" + cleaned.slice(1, -1);
  }

  // Remove $ and commas
  cleaned = cleaned.replace(/[$,]/g, "");

  // Keep only digits, dots, and minus
  cleaned = cleaned.replace(/[^\d.\-]/g, "");

  const num = parseFloat(cleaned);
  return isNaN(num) ? 0 : num;
}

/**
 * Infer transaction category from raw CSV type/details fields
 * (ported from melbudget's type classification)
 */
function inferCategory(
  details: string,
  rawType: string
): { category: string; transactionType: "debit" | "credit" } {
  const d = (details || "").toUpperCase();
  const t = (rawType || "").toUpperCase();

  if (d.includes("CREDIT") || t.includes("ACH_CREDIT")) {
    return { category: "Transfer", transactionType: "credit" };
  }
  if (d.includes("DSLIP") || t.includes("CHECK_DEPOSIT") || t.includes("DEPOSIT")) {
    return { category: "Transfer", transactionType: "credit" };
  }
  if (t.includes("FEE")) {
    return { category: "Bank Fees", transactionType: "debit" };
  }
  if (t.includes("ACH_DEBIT")) {
    return { category: "Payment", transactionType: "debit" };
  }
  if (t.includes("DEBIT_CARD")) {
    return { category: "Payment", transactionType: "debit" };
  }
  if (d.includes("DEBIT")) {
    return { category: "Payment", transactionType: "debit" };
  }

  // Fallback: infer from amount sign (handled by caller)
  return { category: "Payment", transactionType: "debit" };
}

/**
 * Infer expense category from merchant/description name.
 * Uses case-insensitive substring matching against ~150 common merchant patterns.
 * Returns undefined when no match so manual classification can fill in later.
 */
export function inferExpenseCategory(name: string): string | undefined {
  const n = (name || "").toLowerCase();

  const categoryPatterns: { category: string; patterns: string[] }[] = [
    {
      category: "Groceries",
      patterns: [
        "walmart", "whole foods", "trader joe", "kroger", "safeway",
        "albertsons", "publix", "sprouts", "costco", "sam's club",
        "aldi", "food lion", "piggly wiggly", "winco", "h-e-b", "market",
      ],
    },
    {
      category: "Dining",
      patterns: [
        "mcdonald", "burger king", "wendy", "taco bell", "chipotle",
        "panera", "starbucks", "dunkin", "subway", "pizza hut",
        "domino", "chick-fil-a", "popeyes", "sonic", "whataburger",
        "waffle house", "ihop", "applebee", "chili", "olive garden",
        "denny", "buffalo wild wings", "five guys", "jack in the box",
        "panda express", "arby", "little caesars", "papa john",
        "wingstop", "zaxby", "cook out", "raising cane", "in-n-out",
        "shake shack", "restaurant", "cafe", "coffee", "diner",
        "grill", "bistro", "doordash", "uber eats", "grubhub",
      ],
    },
    {
      category: "Gas & Auto",
      patterns: [
        "shell", "chevron", "bp", "exxon", "mobil", "speedway",
        "circle k", "murphy", "quiktrip", "racetrac", "wawa", "sheetz",
        "pilot", "loves", "casey", "valero", "marathon", "sunoco",
        "citgo", "phillips 66", "autozone", "o'reilly", "advance auto",
        "napa", "discount tire", "jiffy lube", "valvoline", "firestone",
        "midas", "car wash",
      ],
    },
    {
      category: "Bills & Utilities",
      patterns: [
        "t-mobile", "at&t", "verizon", "comcast", "xfinity", "spectrum",
        "cox", "centurylink", "frontier", "windstream", "electric",
        "water", "gas company", "sewer", "trash", "waste", "pge",
        "pg&e", "duke energy", "fpl", "dte", "consumers energy",
        "national grid", "dominion", "entergy", "xcel", "internet",
      ],
    },
    {
      category: "Shopping",
      patterns: [
        "amazon", "target", "best buy", "home depot", "lowes", "ikea",
        "wayfair", "bed bath", "tjmaxx", "marshalls", "ross",
        "dollar tree", "dollar general", "family dollar", "big lots",
        "hobby lobby", "michaels", "joann", "bath body", "old navy",
        "gap", "h&m", "zara", "forever 21", "burlington", "nordstrom",
        "macy", "jc penney", "kohl",
      ],
    },
    {
      category: "Health",
      patterns: [
        "cvs", "walgreens", "rite aid", "hospital", "clinic", "doctor",
        "dentist", "dental", "medical", "urgent care", "pharmacy",
        "optometrist", "physical therapy", "quest diagnostics", "labcorp",
      ],
    },
    {
      category: "Subscriptions",
      patterns: [
        // Streaming video
        "netflix", "hulu", "disney+", "disney plus", "hbo max", "paramount",
        "peacock", "apple tv", "crunchyroll",
        // Streaming music
        "spotify", "apple music", "youtube premium", "youtube music",
        "tidal", "pandora", "amazon music",
        // Software / AI / Cloud
        "chatgpt", "openai", "anthropic", "github", "copilot",
        "adobe", "microsoft 365", "office 365", "google one", "icloud",
        "dropbox", "notion", "figma", "canva", "grammarly",
        // VPN / Security
        "nordvpn", "expressvpn", "1password", "lastpass", "bitwarden",
        // Gaming subscriptions
        "xbox game pass", "playstation plus", "ps plus", "ea play",
        // Reading / Learning
        "audible", "kindle unlimited", "scribd", "masterclass",
        // Other digital
        "patreon", "prime", "amazon prime", "walmart+",
        "subscription", "membership",
      ],
    },
    {
      category: "Entertainment",
      patterns: [
        // In-person entertainment (NOT streaming — those are Subscriptions)
        "movie", "theater", "cinema", "amc", "regal",
        "bowling", "arcade", "dave busters", "top golf",
        "concert", "ticketmaster", "stubhub", "eventbrite",
        "gaming", "steam", "xbox", "playstation", "nintendo",
      ],
    },
    {
      category: "Debt",
      patterns: [
        "loan payment", "loan drawdown", "borrow", "repayment",
        "overdraft", "interest charge", "cash app borrow", "weokie",
      ],
    },
  ];

  for (const { category, patterns } of categoryPatterns) {
    for (const pattern of patterns) {
      if (n.includes(pattern)) {
        return category;
      }
    }
  }

  return undefined;
}

/**
 * Map payment channel from details field
 */
function inferChannel(details: string): string {
  const d = (details || "").toUpperCase();
  if (d.includes("DEBIT")) return "in store";
  if (d.includes("ACH") || d.includes("CREDIT")) return "online";
  if (d.includes("CHECK") || d.includes("DSLIP")) return "other";
  return "online";
}

/**
 * Parse date string with common bank formats
 */
function parseDate(dateStr: string, format: string): string {
  if (!dateStr) return new Date().toISOString();

  // A timestamp that carries a time (and often a zone) must be parsed WHOLE.
  // The date-only branches below build `new Date(y, m, d)` = LOCAL MIDNIGHT,
  // which discarded the clock time on every row: a CDT export became a uniform
  // 13:00:00Z. That lost same-day ordering and weakened the dedup fingerprint,
  // which keys on the full timestamp. Cash App exports "2026-08-31 11:34:41 CDT".
  if (/\d{1,2}:\d{2}/.test(dateStr)) {
    const withT = new Date(dateStr.replace(" ", "T"));
    if (!isNaN(withT.getTime())) return withT.toISOString();
    const asIs = new Date(dateStr);
    if (!isNaN(asIs.getTime())) return asIs.toISOString();
  }

  // Try the specified format first
  if (format === "MM/dd/yyyy" || format === "MM/DD/YYYY") {
    const parts = dateStr.split("/");
    if (parts.length === 3) {
      const [month, day, year] = parts;
      const d = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      if (!isNaN(d.getTime())) return d.toISOString();
    }
  }

  if (format === "yyyy-MM-dd" || format === "YYYY-MM-DD") {
    const d = new Date(dateStr);
    if (!isNaN(d.getTime())) return d.toISOString();
  }

  // Fallback: let JS parse it
  const d = new Date(dateStr);
  if (!isNaN(d.getTime())) return d.toISOString();

  return new Date().toISOString();
}

/**
 * Auto-detect column mapping from CSV headers
 */
// Cash App CSV has exactly these headers — if we detect them, use exact mapping
const CASHAPP_PRESET: Record<string, StandardField> = {
  "Transaction ID": "externalId",
  "Date": "date",
  "Transaction Type": "type",
  "Currency": "currency",
  "Amount": "amount",
  "Fee": "fee",
  "Net Amount": "ignore",      // We use Amount + Fee separately
  "Asset Type": "ignore",       // Crypto-only field
  "Asset Price": "ignore",      // Crypto-only field
  "Asset Amount": "ignore",     // Crypto-only field
  "Status": "status",
  "Notes": "description",
  "Name of sender/receiver": "counterparty",
  "Account": "sourceAccount",
};

function isCashAppCSV(headers: string[]): boolean {
  const required = ["Transaction ID", "Date", "Transaction Type", "Amount", "Name of sender/receiver", "Account"];
  return required.every((r) => headers.some((h) => h === r));
}

export function autoDetectMapping(
  headers: string[]
): Partial<Record<string, StandardField>> {
  // Check for Cash App preset first
  if (isCashAppCSV(headers)) {
    const mapping: Partial<Record<string, StandardField>> = {};
    for (const header of headers) {
      mapping[header] = CASHAPP_PRESET[header] || "ignore";
    }
    return mapping;
  }

  const mapping: Partial<Record<string, StandardField>> = {};

  const patterns: { field: StandardField; patterns: RegExp[] }[] = [
    {
      field: "date",
      patterns: [/^date$/i, /posting/i, /trans.*date/i],
    },
    {
      field: "description",
      patterns: [/desc/i, /memo/i, /payee/i, /merchant/i, /narration/i, /^notes$/i],
    },
    {
      field: "counterparty",
      patterns: [/^name of/i, /sender.*receiver/i, /^counterparty$/i, /^contact$/i],
    },
    {
      field: "amount",
      patterns: [/^amount$/i, /^amt$/i, /^value$/i, /^net amount$/i],
    },
    {
      field: "type",
      patterns: [/^type$/i, /trans.*type/i, /^category$/i],
    },
    {
      field: "balance",
      patterns: [/balance/i, /running/i],
    },
    {
      field: "details",
      patterns: [/^details$/i, /^detail$/i],
    },
    {
      field: "status",
      patterns: [/^status$/i],
    },
    {
      field: "fee",
      patterns: [/^fee$/i],
    },
    {
      field: "externalId",
      patterns: [/^transaction id$/i, /^reference/i, /^ref\s*#?$/i],
    },
    {
      field: "sourceAccount",
      patterns: [/^account$/i, /^source account$/i, /^from account$/i],
    },
    {
      field: "currency",
      patterns: [/^currency$/i],
    },
    {
      field: "note",
      patterns: [/^note$/i, /^comment$/i],
    },
    {
      field: "checkNumber",
      patterns: [/check/i, /slip/i],
    },
  ];

  for (const header of headers) {
    for (const { field, patterns: pats } of patterns) {
      if (pats.some((p) => p.test(header))) {
        // Don't overwrite if already matched
        const alreadyMapped = Object.values(mapping).includes(field);
        if (!alreadyMapped) {
          mapping[header] = field;
          break;
        }
      }
    }
  }

  return mapping;
}

/**
 * Normalize CSV rows into bank-app Transaction[] using a column mapping
 */
export function normalizeTransactions(
  rows: Record<string, string>[],
  mapping: CSVMapping
): Transaction[] {
  const transactions: Transaction[] = [];

  for (const row of rows) {
    try {
      // Extract mapped values
      let dateStr = "";
      let description = "";
      let amountStr = "";
      let typeStr = "";
      let details = "";
      let balanceStr = "";
      let counterparty = "";
      let feeStr = "";
      let status = "";
      let externalId = "";
      let sourceAccount = "";
      let currency = "";
      let note = "";

      for (const [csvHeader, standardField] of Object.entries(
        mapping.columnMappings
      )) {
        const val = row[csvHeader] || "";
        switch (standardField) {
          case "date":
            dateStr = val;
            break;
          case "description":
            description = val;
            break;
          case "amount":
            amountStr = val;
            break;
          case "type":
            typeStr = val;
            break;
          case "details":
            details = val;
            break;
          case "balance":
            balanceStr = val;
            break;
          case "counterparty":
            counterparty = val;
            break;
          case "fee":
            feeStr = val;
            break;
          case "status":
            status = val;
            break;
          case "externalId":
            externalId = val;
            break;
          case "sourceAccount":
            sourceAccount = val;
            break;
          case "currency":
            currency = val;
            break;
          case "note":
            note = val;
            break;
        }
      }

      // Build description from counterparty + note when description is empty
      // Cash App: "Notes" maps to description, "Name of sender/receiver" maps to counterparty
      if (!description && note) {
        description = note;
      }
      if (!description && counterparty) {
        description = counterparty;
      }

      // When description is a generic Cash App type, prefer counterparty as primary name
      // "Loan payment", "Cash Card Debit", "Payment" etc. are not useful descriptions
      const GENERIC_DESCRIPTIONS = [
        "loan payment", "cash card debit", "payment", "cash out",
        "cash card refund", "credit", "debit", "boost",
      ];
      const isGeneric = GENERIC_DESCRIPTIONS.includes(description.toLowerCase().trim());
      if (isGeneric && counterparty && counterparty.length > 1) {
        description = `${counterparty} (${description})`;
      } else if (!isGeneric && counterparty && counterparty !== description && counterparty.length > 1) {
        // Append counterparty to non-generic description when both exist
        description = `${description} (${counterparty})`;
      }

      const amount = cleanDecimal(amountStr);
      const parsedBalance = cleanDecimal(balanceStr);
      if (parsedBalance !== 0) _lastParsedBalance = parsedBalance;
      if (amount === 0 && !description) continue; // Skip empty rows

      const { category, transactionType } = inferCategory(details, typeStr);

      // Auto-detect expense category from merchant name
      const detectedExpenseCategory = inferExpenseCategory(description);

      // Override type based on amount sign
      const finalType =
        amount < 0 ? "debit" : amount > 0 ? "credit" : transactionType;

      // Detect transfers - money moving between accounts shouldn't count as income/expense
      const transferResult = detectTransfer(description, typeStr, amount);

      // Detect loan principal flows (Cash App Borrow etc.) by raw Type. Both the
      // disbursement (+) and the principal repayment (-) are loan principal —
      // excluded from income/expense like transfers; only derived fees are an
      // expense. Detected by Type, not name (Borrow rows share a generic name). (#131)
      const isLoanPrincipal = isLoanPrincipalType(typeStr, description);

      const transaction: Transaction = {
        id: crypto.randomUUID(),
        $id: crypto.randomUUID(),
        name: description || "Unknown Transaction",
        paymentChannel: inferChannel(details),
        type: finalType,
        accountId: "",
        amount: Math.abs(amount),
        pending: false,
        // Category at import time. Loans -> Loan/Loan Repayment (#131). Real
        // transfers -> Transfer. Otherwise: a debit uses the inferred category; a
        // credit is money IN, so it's "Income" when classified as such, else the
        // neutral "Deposit" — NOT the old blanket "Transfer" default that
        // mislabeled every paycheck/P2P deposit. (#131, #135)
        category: isLoanPrincipal
          ? (amount > 0 ? "Loan" : "Loan Repayment")
          : transferResult.isTransfer
            ? "Transfer"
            : amount < 0
              ? category
              : detectedExpenseCategory === "Income"
                ? "Income"
                : "Deposit",
        isLoanPrincipal: isLoanPrincipal || undefined,
        date: parseDate(dateStr, mapping.dateFormat),
        image: "",
        $createdAt: new Date().toISOString(),
        channel: inferChannel(details),
        senderBankId: "",
        receiverBankId: "",
        isTransfer: transferResult.isTransfer,
        // The save layer reads `note` and `csvType`; this used to emit `notes`
        // and `transactionType`, so BOTH silently persisted as null on every
        // import — which is why the Notes and Type columns in the transactions
        // table have always been blank. Emit both names: the new ones so the
        // data lands, the old ones so any existing reader keeps working.
        note: note || undefined,
        notes: transferResult.isTransfer ? `Auto: ${transferResult.reason}` : (note || undefined),
        csvType: typeStr || undefined,
        transactionType: typeStr || undefined,
        counterparty: counterparty || undefined,
        fee: cleanDecimal(feeStr) || undefined,
        status: status || undefined,
        externalId: externalId || undefined,
        sourceAccount: sourceAccount || undefined,
        currency: currency || undefined,
        // Keep the untouched row so a column we never mapped is still
        // recoverable — the app never stores the file itself (#179).
        rawRow: row as unknown,
        expenseCategory: detectedExpenseCategory,
      };

      transactions.push(transaction);
    } catch {
      // Skip unparseable rows
      continue;
    }
  }

  // Sort by date descending
  transactions.sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
  );

  return transactions;
}

/** Track parsed balance from CSV rows - stored on transaction as metadata */
let _lastParsedBalance = 0;
export function getLastParsedBalance(): number {
  return _lastParsedBalance;
}

/**
 * Create a synthetic Account from CSV data
 */
export function createAccountFromCSV(
  filename: string,
  transactions: Transaction[]
): Account {
  const id = crypto.randomUUID();

  // Use the last parsed balance from the CSV, or calculate from transactions
  let balance = _lastParsedBalance;
  if (balance === 0 && transactions.length > 0) {
    balance = transactions.reduce((sum, t) => {
      return sum + (t.type === "credit" ? t.amount : -t.amount);
    }, 0);
  }

  const cleanName = filename
    .replace(/\.csv$/i, "")
    .replace(/[_-]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  return {
    id,
    availableBalance: balance,
    currentBalance: balance,
    officialName: cleanName,
    mask: "CSV",
    institutionId: "csv-upload",
    name: cleanName,
    type: "depository",
    subtype: "checking",
    appwriteItemId: id,
    shareableId: id,
  };
}

/**
 * Calculate summary statistics (ported from melbudget)
 * Excludes transfers from income/expense totals for accurate reporting
 */
export function calculateSummary(transactions: Transaction[]): CSVSummary {
  if (transactions.length === 0) {
    return {
      totalTransactions: 0,
      totalSpent: 0,
      totalReceived: 0,
      averageTransaction: 0,
      dateRange: "No transactions",
      transferCount: 0,
      transferVolume: 0,
    };
  }

  let totalSpent = 0;
  let totalReceived = 0;
  let transferCount = 0;
  let transferVolume = 0;

  for (const t of transactions) {
    if (isInternalTransfer(t)) {
      transferCount++;
      transferVolume += t.amount;
      continue; // Don't count transfers in income/expense
    }
    // Loan principal (borrow disbursement + principal repayment) is neither
    // income nor expense — borrowed money isn't earnings and repaying it isn't
    // spending. Only derived loan fees are a cost (shown on the loans page). (#131)
    if (isLoanPrincipalRow(t)) continue;
    if (t.type === "debit") {
      totalSpent += t.amount;
    } else {
      totalReceived += t.amount;
    }
  }

  const dates = transactions
    .map((t) => new Date(t.date))
    .filter((d) => !isNaN(d.getTime()))
    .sort((a, b) => a.getTime() - b.getTime());

  // Explicit zone: an ambient-zone toLocaleDateString renders differently on
  // the UTC server than in the browser, which breaks hydration (#157).
  const fmtDate = (d: Date) =>
    d.toLocaleDateString("en-US", { timeZone: DISPLAY_TIME_ZONE });

  const dateRange =
    dates.length >= 2
      ? `${fmtDate(dates[0])} to ${fmtDate(dates[dates.length - 1])}`
      : dates.length === 1
        ? fmtDate(dates[0])
        : "No dates";

  const realCount = transactions.length - transferCount;

  return {
    totalTransactions: transactions.length,
    totalSpent,
    totalReceived,
    averageTransaction: realCount > 0 ? (totalSpent + totalReceived) / realCount : 0,
    dateRange,
    transferCount,
    transferVolume,
  };
}

/**
 * Generate a deterministic dedup key for a transaction.
 * Used to detect duplicates across CSV re-uploads.
 */
export function generateDedupKey(tx: Transaction): string {
  if (tx.externalId) {
    return `ext:${tx.externalId}`;
  }
  // Composite key from date (day only) + amount + normalized name + note + type.
  // note/type are included because Cash App rows leave `name` empty (descriptor
  // lives in Notes); without them distinct no-name charges collapse to one key
  // and get dropped as false duplicates. Mirrors makeDedupKey in csv.actions.ts. (#125)
  const day = tx.date ? tx.date.slice(0, 10) : "";
  const note = (tx.notes || "").toLowerCase().trim();
  const type = (tx.type || "").toLowerCase().trim();
  return `composite:${day}|${tx.amount}|${tx.name.toLowerCase().trim()}|${note}|${type}`;
}
